import { IconButton, List, ListItem, ListItemText, Menu } from "@mui/material";
import { get } from "lodash";
import moment from "moment";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useParams } from "react-router";
import { FinalTableSpy } from "src/components/FinalTable/FinalTable";
import { Checkbox } from "src/components/Form/CheckboxFieldV2/CheckboxFieldV2";
import { IFormDropdownOption } from "src/components/Form/FormDropdown/FormDropdown";
import { SimpleDatePicker } from "src/components/Form/SimpleDatePickerFieldV2/SimpleDatePickerField";
import FormModal from "src/components/FormModalV2/FormModal";
import { StyledMenuItem } from "src/components/StyledMenuItem/StyledMenuItem";
import FilterContainer from "src/components/TableV2/FilterContainer";
import StatusFilter from "src/components/TableV2/StatusFilter";
import DisTable from "src/components/TableV2/Table";
import TextFilter from "src/components/TableV2/TextFilter";
import useAutosave from "src/hooks/useAutosave";
import { IApplicationRequirement } from "src/store/models/application.model";
import { IRootState } from "src/store/reducers";
import {
  fetchAppRequirementsList,
  fetchReqDetails,
  fetchReqOptions,
  placeOrder,
  saveReqDetails,
} from "src/store/reducers/applicationRequirements";
import { openUrl } from "src/util/util.navigation";
import { formatDate, formatDateOnly } from "src/views/CampaignDetail/utils";
import * as S from "../../../../constants/StringConstants";
import { PageContainerButtons } from "../../PageContainer";
import { TabProps } from "../../pages";

const columns = (placeOrderHandler, selectedReqs, documentSelected, dateChangeHandler) => [
  {
    key: "select",
    title: S.APP_REQS_COL_SELECT,
    render: item => {
      const checked = selectedReqs.some(req => req.id === item.id);
      return (
        <Checkbox
          checked={checked}
          onClick={e => {
            e.stopPropagation();
            documentSelected(item);
          }}
        />
      );
    },
  },

  { key: "description", title: S.APP_REQS_COL_DESCRIPTION, style: { minWidth: "5rem" }, sortKey: "Description" },
  { key: "requirementOf", title: S.APP_REQS_COL_REQ_OF, style: { minWidth: "5rem" }, sortKey: "RequirementOf" },
  {
    key: "ordered",
    title: S.APP_REQS_COL_ORDERED,
    style: { minWidth: "5rem" },
    render: (item: IApplicationRequirement) => formatDate(item.ordered),
    sortKey: "Ordered",
  },
  {
    key: "received",
    title: S.APP_REQS_COL_RECEIVED,
    style: { minWidth: "5rem" },
    render: (item: IApplicationRequirement) => formatDate(item.received),
    sortKey: "Received",
  },
  {
    key: "followUp",
    title: S.APP_REQS_COL_FOLLOW_UP,
    style: { minWidth: "5rem", maxWidth: "8rem" },
    render: (item: IApplicationRequirement, col, index: number) => (
      <div className="applications-req-follow-up">
        <SimpleDatePicker
          name={`followUp.${index}`}
          input={{
            name: `followUp.${index}`,
            value: item.followUp ? moment(item.followUp).toDate() : null,
            onBlur: null,
            onFocus: null,
            onChange: e => {
              dateChangeHandler(e, item);
            },
          }}
          meta={{}}
        />
      </div>
    ),
    renderSort: (item: IApplicationRequirement) => formatDateOnly(item.followUp),
    sortKey: "FollowUp",
  },
  {
    key: "notes",
    title: S.APP_REQS_COL_NOTES,
    style: { minWidth: "5rem", maxWidth: "25rem", whiteSpace: "normal", wordBreak: "break-word" },
    sortKey: "Notes",
  },
  {
    key: "vendor",
    title: S.APP_REQS_COL_VENDOR,
    style: { minWidth: "5rem" },
    sortKey: "Vendor",
  },
  {
    key: "placeOrder",
    title: "",
    style: { minWidth: "5rem" },
    render: item => (
      <a
        target="_blank"
        className="app-docs__action-file-btn"
        href={item.vendor}
        onClick={e => {
          e.preventDefault();
          e.stopPropagation();
          placeOrderHandler(item);
        }}>
        {S.APP_REQS_PLACE_ORDER}
      </a>
    ),
  },
];

export const AppRequirements = (props: TabProps) => {
  const tableRef = useRef<any>(null);
  const { id: appId } = useParams<{ id: string }>();
  const [selectedRequirement, setSelectedRequirement] = useState<IApplicationRequirement | null>(null);
  const [selectedRequirements, setSelectedRequirements] = useState<IApplicationRequirement[]>([]);
  const dispatch = useDispatch();
  const history = useHistory();

  const [openModal, setOpenModal] = React.useState(false);

  useEffect(() => {
    dispatch(fetchReqOptions(appId));
  }, []);

  const onPlaceOrder = (item: IApplicationRequirement) => {
    setOpenModal(true);
    setSelectedRequirement(item);
  };

  const { selectVendorList, selectReqOfList } = useSelector((state: IRootState) => ({
    selectVendorList: state.applicationRequirements?.requirementDetails?.selectVendorList || [],
    selectReqOfList: state.applicationRequirements.requirementDetails?.selectRequiredOriginList || [],
  }));

  const fetchOrderURL = async (provider: IFormDropdownOption) => {
    try {
      const res: any = await dispatch(placeOrder(appId, selectedRequirement.id, provider.value));
      openUrl(res.value.data, true);
    } catch (e) {
      alert("Error placing order");
    }
  };

  const AutoSave = useAutosave(props, () => {});

  const [contextMenu, setContextMenu] = React.useState<{
    mouseX: number;
    mouseY: number;
  } | null>(null);

  const handleContextMenu = (event: React.MouseEvent) => {
    event.preventDefault();
    setContextMenu(
      contextMenu === null
        ? {
            mouseX: event.clientX + 2,
            mouseY: event.clientY - 6,
          }
        : // repeated contextmenu when it is already open closes it with Chrome 84 on Ubuntu
          // Other native context menus might behave different.
          // With this behavior we prevent contextmenu from the backdrop to re-locale existing context menus.
          null,
    );

    // Prevent text selection lost after opening the context menu on Safari and Firefox
    const selection = document.getSelection();
    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);

      setTimeout(() => {
        selection.addRange(range);
      });
    }
  };

  const handleClose = () => {
    setContextMenu(null);
  };

  // TODO: improve this hack
  const handleUpdateFollowup = async (num: number, event: any) => {
    setContextMenu(null);
    try {
      await Promise.all(
        selectedRequirements.map(async req => {
          const result = await dispatch(fetchReqDetails(`${req.id}`));
          const payload = get(result, "value.data") as IApplicationRequirement;
          const newFollowUpDate =
            num !== -1
              ? moment(payload.followUp || moment().format("YYYY-MM-DD"))
                  .add(num, "days")
                  .format("YYYY-MM-DD")
              : null;
          payload.followUp = newFollowUpDate;
          await dispatch(saveReqDetails(appId, payload));
        }),
      );
    } catch (e) {
      alert("Error updating follow-up date");
    }
    if (tableRef.current) {
      tableRef.current.update();
    }
    setSelectedRequirements([]);
  };

  const handleDateChange = async (date: string, req: IApplicationRequirement) => {
    try {
      const result = await dispatch(fetchReqDetails(`${req.id}`));
      const payload = get(result, "value.data") as IApplicationRequirement;
      const newFollowUpDate = moment(date).format("YYYY-MM-DD");
      payload.followUp = newFollowUpDate;
      await dispatch(saveReqDetails(appId, payload));
    } catch (e) {
      alert("Error updating follow-up date");
    }
  };

  return (
    <div>
      <FormModal open={openModal} saveLabel={null} onCancel={() => setOpenModal(false)} values={{}} onSubmit={() => {}}>
        <List>
          {selectVendorList.map(provider => (
            <React.Fragment key={provider.value}>
              <ListItem
                secondaryAction={
                  <IconButton
                    edge="end"
                    aria-label="select"
                    onClick={() => {
                      fetchOrderURL(provider);
                      setOpenModal(false);
                    }}>
                    <button className="button-v2 tiny orange">{S.APP_AGENTS_USE_LABEL}</button>
                  </IconButton>
                }>
                <ListItemText primary={provider.key} primaryTypographyProps={{ fontSize: "14px" }} />
              </ListItem>
              <hr />
            </React.Fragment>
          ))}
        </List>
      </FormModal>
      <DisTable<IApplicationRequirement>
        getRef={ft => {
          tableRef.current = ft;
        }}
        columns={columns(
          onPlaceOrder,
          selectedRequirements,
          requirement => {
            const checked = selectedRequirements.some(req => req.id === requirement.id);
            if (!checked) {
              setSelectedRequirements([...selectedRequirements, requirement]);
            } else {
              setSelectedRequirements(selectedRequirements.filter(req => req.id !== requirement.id));
            }
          },
          handleDateChange,
        )}
        initialState={{}}
        rowClassName={(data: IApplicationRequirement) => {
          return data.priority ? "priority" : "";
        }}
        onRowClick={(data: IApplicationRequirement, event: any, tableData) => {
          const tag = event.target.tagName;
          if (tag !== "TD") {
            return;
          }
          const { total, data: requirements } = tableData;
          const ids = requirements.map(req => req.id);
          history.push(`${location.pathname}/${data.id}?requirementList=${ids}&hasMore=${total > ids.length}`);
        }}
        onUpdate={async ({ page, rows, sort, query }) => {
          const result: any = await dispatch(fetchAppRequirementsList({ appId, page, rows, sort, query }));
          return {
            total: get(result, "value.data.totalRows", 0) as number,
            data: get(result, "value.data.items", []) as IApplicationRequirement[],
          };
        }}>
        <FilterContainer
          left={
            <>
              <strong>{S.APP_FILTERS}</strong>

              <TextFilter
                fields={[
                  { type: "text", name: "description", label: S.APP_REQS_COL_DESCRIPTION },
                  { type: "text", name: "notes", label: S.APP_REQS_COL_NOTES },
                ]}
                inputProps={{ style: { width: "130px", height: "36px", borderRadius: "20px" } }}
              />
            </>
          }
          right={
            <>
              <StatusFilter
                key="requirementOf"
                fieldName="requirementOf"
                fieldLabel={S.APP_REQS_COL_REQ_OF}
                statusList={selectReqOfList}
              />
              <StatusFilter
                key="vendorTypeID"
                fieldName="vendorTypeID"
                fieldLabel={S.APP_REQS_COL_VENDOR}
                statusList={selectVendorList}
              />

              <FinalTableSpy>
                {props => (
                  <button className="button-v2 gray small" onClick={() => props.update({ page: 1, query: {} })}>
                    {S.APP_FILTERS_RESET}
                  </button>
                )}
              </FinalTableSpy>
            </>
          }></FilterContainer>
      </DisTable>
      <PageContainerButtons>
        {selectedRequirements.length > 0 ? (
          <>
            <button className="button-v2 button-v2__white" onClick={handleContextMenu}>
              {S.APP_REQS_BULK_EDIT}
            </button>
            &nbsp; &nbsp;
          </>
        ) : null}
        <button className="button-v2 button-v2__secondary" onClick={() => openUrl(`${location.pathname}/new`)}>
          {S.APP_REQS_ADD_BUTTON_TITLE}
        </button>
      </PageContainerButtons>
      <AutoSave />
      <Menu
        open={contextMenu !== null}
        onClose={handleClose}
        anchorReference="anchorPosition"
        anchorPosition={contextMenu !== null ? { top: contextMenu.mouseY, left: contextMenu.mouseX } : undefined}>
        <StyledMenuItem sx={{ fontSize: "14px" }} onClick={handleUpdateFollowup.bind(null, 1)}>
          {S.APP_REQS_FOLLOWUP_ADVANCE_1}
        </StyledMenuItem>
        <StyledMenuItem sx={{ fontSize: "14px" }} onClick={handleUpdateFollowup.bind(null, 3)}>
          {S.APP_REQS_FOLLOWUP_ADVANCE_3}
        </StyledMenuItem>
        <StyledMenuItem sx={{ fontSize: "14px" }} onClick={handleUpdateFollowup.bind(null, 7)}>
          {S.APP_REQS_FOLLOWUP_ADVANCE_7}
        </StyledMenuItem>
        <StyledMenuItem sx={{ fontSize: "14px" }} onClick={handleUpdateFollowup.bind(null, -1)}>
          {S.APP_REQS_FOLLOWUP_CLEAR}
        </StyledMenuItem>
      </Menu>
    </div>
  );
};
